import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  registerServiceWorker,
  subscribeUserToPush,
  sendSubscriptionToServer,
  getOrCreateDeviceName,
} from "../utils/push.js";
import { cancelPushSubscription } from "../utils/pushCleanup.js";

const PushContext = createContext(null);

export const PushProvider = ({ children }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { user, collaborator, customer, businesses, settings, loading } = useAuth();
  const pushNotificationsEnabled = settings?.notification?.push_notifications_enabled ?? false;
  // Prevents overlapping subscribe() runs: a re-render mid-flight would
  // otherwise start a second concurrent subscription.
  const subscribingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const subscribe = async () => {
      if (
        subscribingRef.current ||
        !pushNotificationsEnabled ||
        !user ||
        !collaborator?.uuid ||
        !customer?.uuid ||
        !businesses?.length ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        return;
      }

      subscribingRef.current = true;
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        await registerServiceWorker();
        const registration = await navigator.serviceWorker.ready;
        const  subscription  = await subscribeUserToPush(registration);
        const subJson = subscription.toJSON();
        const deviceName = getOrCreateDeviceName(collaborator.uuid);
        await sendSubscriptionToServer(
          {
            collaborator_uuid: collaborator.uuid,
            customer_uuid: customer.uuid,
            business_uuid: businesses.length > 1 ? null : businesses[0].uuid,
          },
          subJson,
          deviceName,
        );
      if (!cancelled) setIsSubscribed(true);
      } catch (error) {
        console.error("Push subscription failed:", error);
      } finally {
        subscribingRef.current = false;
      }
    };

    const unsubscribe = async () => {
      setIsSubscribed(false);
      await cancelPushSubscription();
    };

    if (loading) return;

    if (user) subscribe();
    else unsubscribe();

    return () => {
      cancelled = true;
    };
  }, [user, collaborator, customer, businesses, pushNotificationsEnabled, loading]);

  return (
    <PushContext.Provider value={{ isSubscribed }}>
      {children}
    </PushContext.Provider>
  );
};

export const usePush = () => useContext(PushContext);
